import React, { useState } from 'react'
import { Logout, Password, MoreVert } from '@mui/icons-material'
import {
  Avatar,
  Box,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { PAGE_URLS } from '../../../utils/helpers/helper'
import ChangeOrganisationPopup from '../leftMenu/ChangeOrganisationPopup'
import TwoFactorAuthentication from '../../../pages/auth/TwoFactorAuthentication'
import { useAuthContext } from '../../../context/auth/AuthContextProvider'
import { useNotificationCounterContext } from '../../../context/notificationsCounterContext/NotificationsCounterContextProvider'

const UserAvatar = ({ navMenuOpen }: { navMenuOpen: boolean }) => {
  const { stopRefresh: stopNotificationRefresh } =
    useNotificationCounterContext()
  const { user, userSignOut } = useAuthContext()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const open = Boolean(anchorEl)

  const stringAvatar = () => {
    const name = user.name
    const firstLetter = name.split(' ')[0][0].toUpperCase()
    const lastLetter = name.split(' ')[1]
      ? name.split(' ')[1][0].toUpperCase()
      : ''

    return `${firstLetter}${lastLetter}`
  }

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleChangePassword = () => {
    handleClose()
    navigate(PAGE_URLS.CHANGE_PASSWORD)
  }

  const handleLogout = () => {
    stopNotificationRefresh()
    userSignOut()
    navigate('/')
  }

  return (
    <React.Fragment>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          whiteSpace: navMenuOpen ? 'normal' : 'nowrap',
        }}
      >
        <IconButton
          onClick={handleAvatarClick}
          size="small"
          sx={{ minWidth: '56px' }}
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <Avatar sx={{ width: '24px', height: '24px', fontSize: '.75rem' }}>
            {stringAvatar()}
          </Avatar>
        </IconButton>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            whiteSpace: navMenuOpen ? 'normal' : 'nowrap',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              marginLeft: 2,
              opacity: navMenuOpen ? 1 : 0,
              transition: 'all var(--global-transition-timing)',
            }}
          >
            <Typography fontSize={14} width={'100%'}>
              {user.name}
            </Typography>
            <Typography fontSize={12}>
              {user.companyName.length > 0
                ? user.companyName
                : user.organisationName}
            </Typography>
          </Box>
          <IconButton onClick={handleAvatarClick}>
            <MoreVert sx={{ color: 'var(--white)' }} />
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="user-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              '&:before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                left: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        keepMounted
      >
        <MenuItem onClick={handleChangePassword}>
          <ListItemIcon>
            <Password fontSize="small" />
          </ListItemIcon>
          Change password
        </MenuItem>

        <TwoFactorAuthentication onClick={() => setAnchorEl(null)} />

        {user.roles.find((role: string) => role === 'ROLE_SUPER_ADMIN') && (
          <ChangeOrganisationPopup onClick={() => setAnchorEl(null)} />
        )}

        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Sign Out
        </MenuItem>
      </Menu>
    </React.Fragment>
  )
}

export default UserAvatar
